import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  IconButton,
  tableCellClasses,
  Autocomplete,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import styled_ from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { 
  fetchLowStockSummaryReport, 
  downloadLowStockSummaryReport 
} from '../../store/reportStore/reportThunk';
import { LowStockItem, LowStockResponse, LowStockPostResponse, InventoryReportFetch } from '../../utils/report.types';
import { ApiStatus } from '../../utils/api.types';
import { toast } from 'react-toastify';
import { Utils } from '../../utils/common-utils';
import ReusableReportHeader from './ReusableReportHeader';
import { IOptions } from '../FormFields/CustomSelect';
import LocationReportFilter from '../Inventory/pickers/LocationReportFilter';
import { useReportLocationId } from '../Inventory/pickers/useReportLocationId';

// Styled components - GSTR-2 Purchase Summary Style
const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0,
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  // Using the same empty style as GSTR-2 report
}));

interface LowStockSummaryProps {
  type: string;
  rawData?: LowStockResponse | LowStockPostResponse;
}

const LowStockSummary: React.FC<LowStockSummaryProps> = ({ type, rawData }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(state => state.reports);
  const { selectedAccount } = useAppSelector(state => state.auth);
  
  // State management
  const [reportData, setReportData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState('');
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [selectedSearchItem, setSelectedSearchItem] = useState<any>(null);
  const [sortBy, setSortBy] = useState('itemName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [stockFilter, setStockFilter] = useState('All');
  const [locationId, setLocationId] = useReportLocationId(); // "" = All Locations
  const [priceVisibility, setPriceVisibility] = useState({
    mrp: true,
    salePrice: true,
    purchasePrice: true
  });

  // Create filter string
  const createFilter = useCallback(() => {
    let filterStr = '';
    
    if (selectedSearchItem) {
      filterStr += `search=${encodeURIComponent(selectedSearchItem.name)}&`;
    }
    
    filterStr += `sortBy=${sortBy}&sortOrder=${sortOrder}&stockFilter=${stockFilter}`;
    
    return filterStr;
  }, [selectedSearchItem, sortBy, sortOrder, stockFilter]);

  // Update filters when dependencies change
  useEffect(() => {
    const newFilters = createFilter();
    setFilters(newFilters);
  }, [createFilter]);

  // Initialize filters on component mount
  useEffect(() => {
    if (!filters) {
      const initialFilters = createFilter();
      setFilters(initialFilters);
    }
  }, []);

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!selectedAccount?._id) return;
    
    setIsLoading(true);
    
    // Map stockFilter to backend expected parameters
    let backendParams: InventoryReportFetch = {
      accountId: selectedAccount._id,
      search: selectedSearchItem?.name || undefined,
      locationId: locationId || undefined,
      sortBy,
      sortOrder
    };

    // Map frontend stockFilter to backend parameters
    if (stockFilter === 'All') {
      backendParams.showAllItems = 'true';
    } else {
      backendParams.showAllItems = 'false';
      backendParams.stockStatus = stockFilter;
    }

    const params = backendParams;

    console.log('Fetching with params:', params);

    try {
      const response = await dispatch(fetchLowStockSummaryReport(params)).unwrap();
      setReportData(response);
      console.log('Response received:', response);
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.message || 'Failed to fetch low stock summary report');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedAccount, selectedSearchItem, locationId, sortBy, sortOrder, stockFilter]);

  // Load data on component mount or filter changes
  useEffect(() => {
    if (rawData) {
      setReportData(rawData);
      setIsLoading(false);
    } else {
      fetchData();
    }
  }, [rawData, fetchData]);

  // Set search options for autocomplete
  useEffect(() => {
    const items = reportData?.data?.items || reportData?.reportData || [];
    if (items.length > 0) {
      // Batch rows repeat the item — one option per SKU.
      const bySku = new Map<string, any>();
      items.forEach((item: any) => {
        const id = item.skuId || item._id;
        if (!bySku.has(id)) bySku.set(id, { _id: id, name: item.itemName, code: item.itemCode, category: item.categoryName });
      });
      const searchItems = Array.from(bySku.values());
      setSearchOptions(searchItems);
    }
  }, [reportData]);



  // Handle download functions
  const handleDownloadPDF = async () => {
    if (!selectedAccount?._id) return false;
    
    // Map stockFilter to backend expected parameters
    let backendParams: InventoryReportFetch = {
      accountId: selectedAccount._id,
      search: selectedSearchItem?.name || undefined,
      locationId: locationId || undefined,
      sortBy,
      sortOrder,
      isDownloadPdf: 'true'
    };

    // Map frontend stockFilter to backend parameters
    if (stockFilter === 'All') {
      backendParams.showAllItems = 'true';
    } else {
      backendParams.showAllItems = 'false';
      backendParams.stockStatus = stockFilter;
    }

    const params = backendParams;
    
    const response = await dispatch(downloadLowStockSummaryReport(params)).unwrap();
    if (response) {
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `low_stock_summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedAccount?._id) return false;
    
    // Map stockFilter to backend expected parameters
    let backendParams: InventoryReportFetch = {
      accountId: selectedAccount._id,
      search: selectedSearchItem?.name || undefined,
      locationId: locationId || undefined,
      sortBy,
      sortOrder,
      isDownloadExcel: 'true'
    };

    // Map frontend stockFilter to backend parameters
    if (stockFilter === 'All') {
      backendParams.showAllItems = 'true';
    } else {
      backendParams.showAllItems = 'false';
      backendParams.stockStatus = stockFilter;
    }

    const params = backendParams;
    
    const response = await dispatch(downloadLowStockSummaryReport(params)).unwrap();
    if (response) {
      const blob = new Blob([response], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `low_stock_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  // Handle filter functions
  const handleRefreshFilters = () => {
    setSelectedSearchItem(null);
    setLocationId('');
    setSortBy('itemName');
    setSortOrder('asc');
    setStockFilter('All');
  };

  // Handle search item selection
  const handleSearchItemChange = (event: any, value: any) => {
    setSelectedSearchItem(value);
  };


  // Get stock status with color
  const getStockStatusText = (status: string) => {
    const statusColors = {
      'Out of Stock': '#d32f2f',
      'Low Stock': '#f57c00',
      'In Stock': '#2e7d32'
    } as const;

    const color = statusColors[status as keyof typeof statusColors] || statusColors['In Stock'];
    
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 600,
          color: color,
          fontSize: '9pt'
        }}
      >
        {status}
      </Typography>
    );
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {!isLoading && status !== ApiStatus.LOADING ? (
        <>
          {/* Report Header */}
          <ReusableReportHeader
            reportType="LOW_STOCK_SUMMARY"
            title="Low Stock Summary Report"
            subtitle="Items running low on stock or out of stock"
            recordCount={reportData?.data?.items?.length || reportData?.reportData?.length || 0}
            isLoading={isLoading}
            onDownloadPDF={handleDownloadPDF}
            onDownloadExcel={handleDownloadExcel}
            showPrint={false}
            showShare={false}
          />

          <div style={{ marginBottom: 12 }} id='pdfContainer'>
            {/* Header and Summary Section - GSTR-2 Style */}
            <Grid container p={2} style={{ borderBottom: '.5px solid #9da7b7', marginBottom: 0 }}>
              <Grid xs={10} >
                {/* <Typography style={{
                  fontSize: 30,
                  fontWeight: 600,
                  marginBottom: '8px'
                }}>
                  Low Stock Summary
                </Typography>
                <Typography style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#6b7280',
                  marginBottom: '16px'
                }}>
                  Inventory items requiring attention
                </Typography> */}
                
                {/* Stock Status Legend */}
                {/* <Box style={{ marginTop: '0px', marginBottom: '16px' }}>
                  <Typography style={{ fontSize: 12, fontWeight: 600, marginBottom: '8px', color: '#374151' }}>
                    Stock Status Legend:
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Chip label="In Stock" size="small" style={{ backgroundColor: '#2e7d32', color: 'white', fontSize: '8pt' }} />
                      <span style={{ fontSize: '10pt', color: '#6b7280' }}>Available items</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Chip label="Low Stock" size="small" style={{ backgroundColor: '#f57c00', color: 'white', fontSize: '8pt' }} />
                      <span style={{ fontSize: '10pt', color: '#6b7280' }}>Running low</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Chip label="Out of Stock" size="small" style={{ backgroundColor: '#d32f2f', color: 'white', fontSize: '8pt' }} />
                      <span style={{ fontSize: '10pt', color: '#6b7280' }}>Need restocking</span>
                    </div>
                  </div>
                </Box> */}

                {/* Filters - Party Ledger Style Exact Copy */}
                <Box style={{ display: 'flex', alignItems: 'center',  width: '100%' }}>
                  <IconButton onClick={handleRefreshFilters}
                    style={{ marginRight: '15px',  color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1' }}
                    size='small' ><RefreshIcon /></IconButton>

                  <Autocomplete
                    disablePortal
                    id="search-items"
                    value={selectedSearchItem || null}
                    style={{ width: 300, marginRight: '10px' }}
                    onChange={handleSearchItemChange}
                    options={searchOptions}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => <TextField {...params}
                      variant="standard"
                      name="search"
                      size='small'
                      placeholder="Type here to search items ..."
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      className='c-text-field-3'
                    />}
                  />

                  {/* Location filter — hidden automatically for single-location accounts */}
                  <LocationReportFilter borderless width={180} value={locationId} onChange={(v: string) => setLocationId(v)} label="Location" />

                  <Select
                    name="sort"
                    disableUnderline
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: "1.5px solid #e9ecf1",
                          boxShadow: 'none',
                          borderRadius: '5px'
                        }
                      }
                    }}
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order as 'asc' | 'desc');
                    }}
                    sx={{
                      boxShadow: "none",
                      width: 200,
                      marginRight: '10px',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                    }}
                    label="Sort Options"
                    className='c-text-field-3'
                    variant="outlined"
                  >
                    <MenuItem value="itemName-asc">Name: A to Z</MenuItem>
                    <MenuItem value="itemName-desc">Name: Z to A</MenuItem>
                    <MenuItem value="currentStock-asc">Stock: Low to High</MenuItem>
                    <MenuItem value="currentStock-desc">Stock: High to Low</MenuItem>
                    <MenuItem value="salePrice-asc">Price: Low to High</MenuItem>
                    <MenuItem value="salePrice-desc">Price: High to Low</MenuItem>
                  </Select>

                  <Select
                    name="stockFilter"
                    disableUnderline
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: "1.5px solid #e9ecf1",
                          boxShadow: 'none',
                          borderRadius: '5px'
                        }
                      }
                    }}
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                    sx={{
                      boxShadow: "none",
                      width: 150,
                      marginRight: '10px',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                    }}
                    label="Stock Filter"
                    className='c-text-field-3'
                    variant="outlined"
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="In Stock">In Stock</MenuItem>
                    <MenuItem value="Low Stock">Low Stock</MenuItem>
                    <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                  </Select>
                </Box>
              </Grid>
              {/* <Grid xs={2} >
                <LoadingButton
                  className={'buttonContainedRoot'}
                  size='small'
                  type="button"
                  name={'submit'}
                  onClick={() => {}}
                  loading={isLoading}
                  variant="contained"
                  sx={{ minWidth: 160 }}
                >
                  Send To Account Email
                </LoadingButton>
              </Grid> */}
            </Grid>


          {/* Additional Info Cards */}
          {/* {reportData?.data?.summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={1} sx={{ border: '1px solid #e3f2fd' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h5" color="info.main" gutterBottom>
                      {Utils.formatNumber(reportData.data.summary.totalStockValue || 0, true, true)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Stock Value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
         
          
            </Grid>
          )} */}

          {/* Data Table - GSTR-2 Purchase Summary Style */}
              <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size='small' aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell width={200}>Item Details</StyledTableCell>
                    <StyledTableCell>Batch / Expiry</StyledTableCell>
                    <StyledTableCell>Item Code</StyledTableCell>
                    <StyledTableCell>HSN Code</StyledTableCell>
                    <StyledTableCell>Stock Status</StyledTableCell>
                    <StyledTableCell>Current Stock</StyledTableCell>
                    <StyledTableCell>Low Stock Qty</StyledTableCell>
                    {/* Reorder Qty hidden — no input for it on the item form yet */}
                    {/* <StyledTableCell>Reorder Qty</StyledTableCell> */}
                    <StyledTableCell>At Location</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading...
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ) : (reportData?.data?.items?.length || reportData?.reportData?.length) ? (
                    (reportData?.data?.items || reportData?.reportData || []).map((item: any) => (
                      <StyledTableRow key={item._id}>
                        <StyledTableCell>{item.itemName}</StyledTableCell>
                        <StyledTableCell>
                          {item.batchNumber || '-'}
                          {item.expiryDate && (
                            <Typography sx={{ fontSize: '8pt', color: '#7a8290' }}>
                              Exp {new Date(item.expiryDate).toLocaleDateString('en-GB')}
                            </Typography>
                          )}
                        </StyledTableCell>
                        <StyledTableCell>{item.itemCode || '-'}</StyledTableCell>
                        <StyledTableCell>{item.hsnCode || '-'}</StyledTableCell>
                        <StyledTableCell>{getStockStatusText(item.stockStatus)}</StyledTableCell>
                        <StyledTableCell>{item.currentStock} {item.unitCode ? `/ ${item.unitCode}` : ''}</StyledTableCell>
                        <StyledTableCell>{item.lowStockQuantity} {item.unitCode ? `/ ${item.unitCode}` : ''}</StyledTableCell>
                        {/* <StyledTableCell>{item.reorderQuantity || 0} {item.unitCode ? `/ ${item.unitCode}` : ''}</StyledTableCell> */}
                        <StyledTableCell>{item.atLocation || '-'}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <TableRow>
                      <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No {stockFilter === 'All' ? 'items' : stockFilter.toLowerCase() + ' items'} found
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

          </div>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          {status === ApiStatus.FAILED ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load low stock summary report. Please try again.
            </Alert>
          ) : (
            <CircularProgress color="info" />
          )}
        </Box>
      )}
    </Box>
  );
};

export default LowStockSummary;